import type { CredentialExposure, SecretEnvironment } from '@developerz/domain';
import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { createMutation } from '../lib/mutation';
import {
  deleteRepoSecret,
  putRepoSecretsDotenv,
  type RepoSecretRow,
  useRepoSecrets,
} from '../lib/repoSecrets';
import { createToast } from '../lib/toast';
import { AddRepoSecretModal } from './AddRepoSecretModal';
import { ConfirmDialog } from './ConfirmDialog';
import { QueryBoundary } from './QueryBoundary';
import { RepoSecretsView } from './RepoSecretsView';
import { Skeleton } from './Skeleton';
import { Toast } from './Toast';

// Repo detail → Secrets (arc 101 slice 06, doc 06). Container: fetches
// `lib/repoSecrets.ts`'s list query, drives the add (PUT, whole `.env` paste)
// and delete (DELETE, one name/environment pair) mutations, and re-reads the
// list after each — never trusts a write's own echo over a fresh GET.
// `fullName` is a plain prop (not derived from route params) so tests can inject
// one directly, same convention as `RepoPromptCard`.

export type RepoSecretsTabProps = {
  readonly fullName: string;
  readonly token: string | null;
};

export function RepoSecretsTab(props: RepoSecretsTabProps): JSX.Element {
  const query = useRepoSecrets(() => props.fullName, props.token);
  const { toast, notify, dismiss } = createToast();

  const [adding, setAdding] = createSignal(false);
  const [deleting, setDeleting] = createSignal<RepoSecretRow | null>(null);

  const submitAdd = createMutation({
    call: (input: {
      environment: SecretEnvironment;
      exposure: CredentialExposure;
      dotenv: string;
    }) => putRepoSecretsDotenv(props.fullName, input, props.token),
    fallback: t('repo_secrets.add_error'),
    onSuccess: () => {
      setAdding(false);
      notify(t('repo_secrets.add_success'));
    },
    onSettled: () => query.refetch(),
  });

  // Reports through the page's `<Toast/>` rather than an inline notice, so the
  // failure copy rides `onError` instead of `error()`.
  const confirmDelete = createMutation({
    call: (row: RepoSecretRow) =>
      deleteRepoSecret(props.fullName, row.name, row.environment, props.token),
    fallback: t('repo_secrets.delete_error'),
    onSuccess: () => {
      setDeleting(null);
      notify(t('repo_secrets.delete_success'));
    },
    onError: (message) => notify(message, 'error'),
    onSettled: () => query.refetch(),
  });

  return (
    <>
      <QueryBoundary query={query} loading={<Skeleton height="16rem" />}>
        {(page) => (
          <RepoSecretsView
            secrets={page.data}
            onAdd={() => setAdding(true)}
            onDeleteRequest={(row) => setDeleting(row)}
          />
        )}
      </QueryBoundary>
      <AddRepoSecretModal
        open={adding()}
        busy={submitAdd.pending()}
        error={submitAdd.error()}
        onSubmit={(input) => void submitAdd.run(input)}
        onClose={() => {
          setAdding(false);
          submitAdd.clearError();
        }}
      />
      <ConfirmDialog
        open={deleting() !== null}
        title={t('repo_secrets.delete_title')}
        desc={t('repo_secrets.delete_body', {
          name: deleting()?.name ?? '',
          environment: deleting()?.environment ?? '',
        })}
        cancelLabel={t('repo_secrets.cancel')}
        actionLabel={t('repo_secrets.delete')}
        destructive
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          const row = deleting();
          if (row) void confirmDelete.run(row);
        }}
      />
      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'success'}
        onDismiss={dismiss}
      />
    </>
  );
}
